import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";

const BASE_URL = "https://api.kennapartners.com/api/v1"; // Replace with your actual base URL

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [formData, setFormData] = useState({
    designation: "",
    group: "Managing counsel"
  });

  const groupOptions = [
    { value: "Managing counsel", label: "Managing Counsel" },
    { value: "Non counsel", label: "Non Counsel" }
  ];

  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    filterRoles();
  }, [roles, searchTerm]);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/roles/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle both possible response structures
      const rolesData = data.data?.roles || [];
      setRoles(rolesData);
      setError("");
    } catch (error) {
      console.error("Error fetching roles:", error);
      setError("Failed to fetch roles. Please try again.");
      setRoles([]); // Reset roles on error
    } finally {
      setLoading(false);
    }
  };

  const filterRoles = () => {
    let filtered = roles;

    if (searchTerm) {
      filtered = filtered.filter(
        (role) => role.designation.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setRoles(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      designation: "",
      group: "Managing counsel"
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    };
    
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

const handleCreateRole = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const token = localStorage.getItem("access_token");
    const payload = {
      designation: formData.designation,
      group: formData.group
    };
    const headers = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    // Log the request payload and content type
    console.log("Sending data to backend:", JSON.stringify(payload));
    console.log("Content-Type:", headers["Content-Type"]);

    const response = await fetch(`${BASE_URL}/roles/`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Try to parse error response
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      throw new Error(errorData.message || "Failed to create role");
    }

    const data = await response.json();
    setRoles((prev) => [...prev, data.data.role]);
    setShowCreateModal(false);
    resetForm();
    setError("");
  } catch (error) {
    console.error("Error creating role:", error);
    setError(error.message.includes('Failed to fetch') 
      ? "Network error: Could not connect to server. Please check your connection or contact support."
      : error.message || "Failed to create role. Please try again."
    );
  } finally {
    setLoading(false);
  }
};

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
  
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Authentication token not found");
      }
  
      const payload = {
        designation: formData.designation,
        group: formData.group
      };
  
      const response = await fetch(`${BASE_URL}/roles/${selectedRoleId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update role");
      }
  
      const updatedRole = {
        id: selectedRoleId,
        designation: formData.designation,
        group: formData.group,
        ...roles.find(role => role.id === selectedRoleId)
      };
  
      setRoles(prev => 
        prev.map(role => 
          role.id === selectedRoleId ? updatedRole : role
        )
      );
      
      setShowEditModal(false);
      setSelectedRoleId(null);
      resetForm();
  
    } catch (error) {
      console.error("Update error:", {
        error: error.message,
        payload: formData,
        roleId: selectedRoleId
      });
      setError(error.message || "Failed to update role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    setLoading(true);
    setError("");
  
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        throw new Error("Authentication token not found");
      }
  
      const response = await fetch(`${BASE_URL}/roles/${selectedRoleId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 
          `Failed to delete role (Status: ${response.status})`
        );
      }
  
      // No need to parse response data for DELETE if API returns empty response
      setRoles(prev => prev.filter(role => role.id !== selectedRoleId));
      setShowDeleteModal(false);
      setSelectedRoleId(null);
    } catch (error) {
      console.error("Delete error:", error);
      setError(error.message || "Failed to delete role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (role) => {
    setFormData({
      designation: role.designation || "",
      group: role.group || "Managing counsel"
    });
    setSelectedRoleId(role.id);
    setShowEditModal(true);
  };

  const openDeleteModal = (roleId) => {
    setSelectedRoleId(roleId);
    setShowDeleteModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedRoleId(null);
    resetForm();
    setError("");
  };    

  return (
    <main className="workspace">
      <Breadcrumb title="Roles">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Roles</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button className="float-right text-white" onClick={() => setError("")}>
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Role Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New Role
          </button>
          <button onClick={fetchRoles} className="btn-secondary" disabled={loading}>
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search roles by designation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !roles.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading roles...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover w-full">
                <thead>
                  <tr>
                    <th className="w-1/4">Designation</th>
                    <th className="w-1/4">Group</th>
                    <th className="w-1/4">Created At</th>
                    <th className="w-1/4">Updated At</th>
                    <th className="w-1/4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-8">
                        No roles found. Create your first role!
                      </td>
                    </tr>
                  ) : (
                    roles.map((role) => (
                      <tr key={role.id}>
                        <td className="w-1/4">
                          <div className="font-medium">{role.designation}</div>
                        </td>
                        <td className="w-1/4">
                          <div className="font-medium">{role.group}</div>
                        </td>
                        <td className="w-1/4">
                          {formatDate(role.created_at)}
                        </td>
                        <td className="w-1/4">
                          {formatDate(role.updated_at)}
                        </td>
                        <td className="w-1/4">
                          <div className="flex gap-1">
                            <Button
                              onClick={() => openEditModal(role)}
                              className="btn btn-sm btn-warning"
                              title="Edit Role"
                            >
                              <span className="la la-edit"></span>
                            </Button>
                            <Button
                              color="danger"
                              onClick={() => openDeleteModal(role.id)}
                              className="btn btn-sm btn-danger"
                              title="Delete Role"
                            >
                              <span className="la la-trash"></span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New Role
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <form onSubmit={handleCreateRole}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Designation *
                      </label>
                      <Input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Group *
                      </label>
                      <select
                        name="group"
                        value={formData.group}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {groupOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </span>
                      ) : "Submit"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Edit Role
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <form onSubmit={handleUpdateRole}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Designation *
                      </label>
                      <Input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Group *
                      </label>
                      <select
                        name="group"
                        value={formData.group}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {groupOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b space-x-4">
                    <Button
                      type="button"
                      onClick={closeAllModals}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      Close
                    </Button>
                    <Button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </span>
                      ) : "Update Role"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this role? This action cannot be undone.</p>
                </div>
                <div className="modal-footer flex justify-center gap-4">
                  <button
                    onClick={closeAllModals}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                  >
                    Cancel
                  </button>
                  <Button
                    color="danger"
                    onClick={handleDeleteRole}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete Role"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Roles;
